import { Radio } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { EmptyState } from '../../components/common/EmptyState';
import { PRIORITY_STYLES, CARD } from './config';
import type { DispatchJob } from './hooks';

interface Props {
  jobs: DispatchJob[];
  onAssign: (job: DispatchJob) => void;
  onSelect: (job: DispatchJob) => void;
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (diff < 1) return 'just now';
  if (diff < 60) return `${diff}m`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h`;
  return `${Math.floor(diff / 1440)}d`;
}

export function QueuePanel({ jobs, onAssign, onSelect }: Props) {
  const today = new Date().toISOString().split('T')[0];
  const sorted = [...jobs].sort((a, b) => {
    const pOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
    const pa = pOrder[a.priority ?? 'medium'] ?? 1;
    const pb = pOrder[b.priority ?? 'medium'] ?? 1;
    if (pa !== pb) return pa - pb;
    return (a.scheduled_date ?? a.created_at).localeCompare(b.scheduled_date ?? b.created_at);
  });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 0 10px' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: palette.g800 }}>Queue</div>
        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: jobs.length > 5 ? palette.amberWash : palette.g100, color: jobs.length > 5 ? palette.amber : palette.g500 }}>{jobs.length}</span>
      </div>

      <div>
        {sorted.length === 0 ? (
          <div style={{ padding: '24px 0', opacity: 0.6 }}>
            <EmptyState icon={<Radio size={24} />} title="Queue is clear" subtitle="No jobs waiting for assignment." />
          </div>
        ) : (
          sorted.map(job => {
            const pr = PRIORITY_STYLES[job.priority ?? 'medium'] ?? PRIORITY_STYLES.medium;
            const overdue = job.scheduled_date && job.scheduled_date < today;
            const overdueDays = overdue && job.scheduled_date
              ? Math.floor((new Date(today).getTime() - new Date(job.scheduled_date).getTime()) / 86400000)
              : 0;
            return (
              <div key={job.id} onClick={() => onSelect(job)}
                style={{ ...CARD, borderColor: overdue ? palette.red : palette.g150 }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = palette.accent)}
                onMouseLeave={e => (e.currentTarget.style.borderColor = overdue ? palette.red : palette.g150)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <span style={{ padding: '1px 6px', borderRadius: 3, fontSize: 9, fontWeight: 700, background: pr.bg, color: pr.fg }}>{pr.label}</span>
                  {overdue && <span style={{ padding: '1px 6px', borderRadius: 3, fontSize: 9, fontWeight: 700, background: palette.redWash, color: palette.red }}>OVERDUE ({overdueDays} day{overdueDays === 1 ? '' : 's'})</span>}
                  <span style={{ flex: 1 }} />
                  <span style={{ fontSize: 10, color: palette.g400 }}>waiting {timeAgo(job.created_at)}</span>
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: palette.g800, marginBottom: 2 }}>{job.title}</div>
                {job.client_name && <div style={{ fontSize: 11, color: palette.g500 }}>{job.client_name}</div>}
                {(job.address || job.location) && <div style={{ fontSize: 11, color: palette.g400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.address ?? job.location}</div>}
                {job.scheduled_time && <div style={{ fontSize: 11, color: palette.g500, marginTop: 2 }}>{job.scheduled_date} {job.scheduled_time}</div>}
                <button onClick={e => { e.stopPropagation(); onAssign(job); }} style={{
                  marginTop: 6, width: '100%', padding: '5px 0', borderRadius: 6, border: 'none',
                  background: palette.accent, color: palette.white, fontSize: 11, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}>Assign Crew</button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
